import type { CSSProperties } from 'react'

import type { MtxBlock } from '../spec/types'

/**
 * A two-axis entitlement grid: what is allowed, at which scope, and by whom.
 *
 * Verbatim from `docs/design/reference/blocks/mtx.html`. Rows are the thing
 * being governed (a payment method, an `emi.Capabilities` flag), columns are
 * the axis it is governed along (direction, provider), and every intersection
 * is one cell stating the verdict, the scope that produced it, and a note.
 *
 * The three-line cell is the whole point. Law 16 says a compliance lock
 * outranks an ops setting and must be legible as such — so a cell never shows
 * only "blocked". It shows the resolved verdict AND the scope that won
 * (`global`, `segment`, `customer`, `compliance`), because an operator who
 * cannot see which layer decided will try to change the wrong one. The same
 * shape carries the capability matrix, where a provider capability with no
 * written confirmation renders a dimmed `false` at scope `unconfirmed` rather
 * than a blank: absent is read as false everywhere in go-wallet, and this grid
 * makes that reading explicit instead of leaving a gap to be interpreted.
 *
 * Cells are buttons, but a locked one leads nowhere: it flashes the role the
 * caller lacks. The control stays visible and disabled-looking rather than
 * hidden, so the path to changing it is discoverable even when this session
 * may not take it. An unlocked cell opens the effective-dated composer — never
 * an in-place edit, because entitlement rows are versioned (law 6).
 */
export function Mtx({
  block,
  onFlash,
}: {
  readonly block: MtxBlock
  readonly onFlash: (message: string) => void
}) {
  // The row label is an extra leading column, wider than the value columns
  // because it carries a name plus its subtitle on one line.
  const cols = [{ l: block.rowLabel ?? '', a: '' }].concat(block.cols.map((c) => ({ l: c, a: '' })))
  const gtc = 'minmax(180px,1.2fr) ' + block.cols.map(() => 'minmax(130px,1fr)').join(' ')

  return (
    <div
      style={{ border: '1px solid var(--k-line)', background: 'var(--k-panel)', overflowX: 'auto' }}
    >
      <div style={{ '--gtc': gtc } as CSSProperties}>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'var(--gtc)',
            height: '28px',
            alignItems: 'center',
            borderBottom: '2px solid var(--k-rule)',
            minWidth: 'min-content',
          }}
        >
          {cols.map((c) => (
            <div
              key={c.l}
              data-a={c.a}
              style={{
                padding: '0 9px',
                fontSize: '9.5px',
                letterSpacing: '.07em',
                textTransform: 'uppercase',
                color: 'var(--k-mut)',
                whiteSpace: 'nowrap',
              }}
            >
              {c.l}
            </div>
          ))}
        </div>

        {block.rows.map(([label, sub, cells]) => (
          <div
            key={label}
            style={{
              display: 'grid',
              gridTemplateColumns: 'var(--gtc)',
              alignItems: 'stretch',
              borderBottom: '1px solid var(--k-line)',
              minWidth: 'min-content',
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '7px',
                padding: '6px 9px',
                fontSize: '12px',
                fontWeight: 600,
                whiteSpace: 'nowrap',
              }}
            >
              <span>{label}</span>
              <span
                style={{ fontFamily: 'var(--k-mono)', fontSize: '10px', color: 'var(--k-mut2)' }}
              >
                {sub}
              </span>
            </div>
            {cells.map((cell, ci) => {
              const [value, tone, scope, note, locked, title] = cell
              // A cell's identity is its (row, column) pair — the column name
              // is the natural key, and the positional fallback only fires on
              // a malformed row with more cells than columns.
              const key = block.cols[ci] ?? String(ci)
              return (
                <button
                  key={key}
                  type="button"
                  className="k-hov"
                  onClick={() => {
                    const reason = note !== undefined && note !== '' ? note : 'requires Compliance'
                    onFlash(
                      locked === true
                        ? 'Locked — ' + reason
                        : 'Opens the effective-dated change composer',
                    )
                  }}
                  data-dim={locked === true ? '1' : ''}
                  title={title ?? ''}
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    gap: '2px',
                    padding: '6px 9px',
                    border: 0,
                    borderLeft: '1px solid var(--k-line)',
                    background: 'transparent',
                    font: 'inherit',
                    textAlign: 'left',
                    cursor: 'pointer',
                    color: 'var(--k-ink)',
                  }}
                >
                  <span
                    data-tone={tone}
                    style={{
                      padding: '0 5px',
                      fontSize: '10px',
                      fontWeight: 800,
                      letterSpacing: '.04em',
                      textTransform: 'uppercase',
                    }}
                  >
                    {value}
                  </span>
                  <span
                    style={{
                      fontSize: '10.5px',
                      color: 'var(--k-mut)',
                      fontFamily: 'var(--k-mono)',
                    }}
                  >
                    {scope ?? ''}
                  </span>
                  <span style={{ fontSize: '10.5px', color: 'var(--k-mut2)' }}>{note ?? ''}</span>
                </button>
              )
            })}
          </div>
        ))}
      </div>
    </div>
  )
}
